<template>
    <b-form-group
        :label="label"
        :label-class="labelClass"
        :description="description"
        :invalid-feedback="invalidFeedback === null ? error : invalidFeedback"
        :state="state"
    >
        <form-input-group
            :left-icon="leftIcon"
            :right-icon="rightIcon"
            :right-button="rightButton"
            :left-text="leftText"
            :right-text="rightText"
        >
            <b-input-group-prepend is-text v-if="leftButton">
                <slot name="leftButton"></slot>
            </b-input-group-prepend>
            <b-form-input
                v-on="listeners"
                v-bind="attrs"
                :value="value"
                :state="state"
                :placeholder="floatingLabel || placeholder"
                :class="[
                    rightButton || rightIcon ? '' : 'rounded-right',
                    floatingLabel ? 'floating-label' : '',
                    'ldx-form-input',
                    secure ? 'text-security-disc' : '',
                ]"
                @input="input"
            ></b-form-input>
            <label :if="floatingLabel" :style="{ left: leftIcon || leftButton ? '3.1rem' : '' }">{{
                floatingLabel
            }}</label>
            <b-input-group-append v-if="!isSmall && (rightButton || rightButton2)">
                <b-button
                    :title="rightButton.title"
                    :variant="rightButton.variant || 'default'"
                    :disabled="rightButton.disabled || false"
                    @click="handleRightButtonClick"
                    :pressed="rightButtonState"
                    v-if="rightButton"
                >
                    <font-awesome-icon v-if="rightButton.icon" :icon="rightButton.icon"></font-awesome-icon>
                    <span class="ml-1" v-if="rightButton.icon && rightButton.text" />
                    <span v-if="rightButton.text">{{ rightButton.text }}</span>
                </b-button>
                <b-button
                    :title="rightButton2.title"
                    :variant="rightButton2.variant || 'default'"
                    :disabled="rightButton2.disabled || false"
                    @click="handleRightButton2Click"
                    :pressed="rightButton2State"
                    v-if="rightButton2"
                >
                    <font-awesome-icon v-if="rightButton2.icon" :icon="rightButton2.icon"></font-awesome-icon>
                    <span class="ml-1" v-if="rightButton2.icon && rightButton2.text" />
                    <span v-if="rightButton2.text">{{ rightButton2.text }}</span>
                </b-button>
            </b-input-group-append>
            <b-input-group-append v-if="isSmall && (rightButton || rightButton2)">
                <b-dropdown :text="dropDownText" :variant="rightButton.variant || 'default'">
                    <b-dropdown-item
                        :title="rightButton.title"
                        :variant="rightButton.variant || 'default'"
                        :disabled="rightButton.disabled || false"
                        @click="handleRightButtonClick"
                        :pressed="rightButtonState"
                        v-if="rightButton"
                    >
                        <span v-if="rightButton.text">{{ rightButton.text }}</span>
                    </b-dropdown-item>
                    <b-dropdown-item
                        :title="rightButton2.title"
                        :variant="rightButton2.variant || 'default'"
                        :disabled="rightButton2.disabled || false"
                        @click="handleRightButton2Click"
                        :pressed="rightButton2State"
                        v-if="rightButton2"
                    >
                        <span v-if="rightButton2.text">{{ rightButton2.text }}</span>
                    </b-dropdown-item>
                </b-dropdown>
            </b-input-group-append>
        </form-input-group>
    </b-form-group>
</template>

<script>
import { formFieldMixin } from '../../../mixins/form-field-mixin';
import FormInputGroup from '../form-input-group/form-input-group.vue';

export default {
    name: 'FormInput',
    mixins: [formFieldMixin],
    components: {
        FormInputGroup,
    },
    created() {
        this.onResize();
        window.addEventListener('resize', this.onResize);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize);
    },
    props: {
        value: [String, Number],
        leftIcon: [Object, Array, String],
        rightIcon: [Object, Array, String],
        floatingLabel: [String, Number],
        placeholder: [String, Number],
        rightButton: [Object, Array, String],
        rightButton2: [Object, Array, String],
        labelClass: {
            type: Array,
            default: () => [],
        },
        leftButton: Boolean,
        leftText: String,
        rightText: String,
        dropDownText: {
            type: String,
            default: 'Select',
        },
        degradeOnSmall: {
            type: Boolean,
            default: false,
        },
        invalidFeedback: {
            type: String,
            default: null,
        },
        emptyValue: {
            type: String,
            default: '',
        },
        secure: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            rightButtonState: false,
            rightButton2State: false,
            isSmall: false,
        };
    },
    methods: {
        input(value) {
            if (value === '') {
                this.$emit('input', this.emptyValue);
                this.$emit('modelCompat:input', this.emptyValue);
            } else {
                this.$emit('input', value);
                this.$emit('modelCompat:input', value);
            }
        },
        handleRightButtonClick() {
            if (this.rightButton.toggle) {
                this.rightButtonState = !this.rightButtonState;
            }
            this.$emit('handle-right-button-click', this.rightButtonState);
        },
        handleRightButton2Click() {
            if (this.rightButton2.toggle) {
                this.rightButton2State = !this.rightButton2State;
            }
            this.$emit('handle-right-button-2-click', this.rightButton2State);
        },
        clearButtonsStates() {
            this.rightButtonState = false;
            this.rightButton2State = false;
        },
        onResize() {
            this.isSmall = window.innerWidth < 768 && this.degradeOnSmall;
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../../../mixins.scss';
@import '../../../variables.scss';

@mixin activeLabel() {
    z-index: 99;
    font-size: 0.8rem;
    line-height: 1.1;
    padding: 0.1rem;
    margin-left: 0.1rem;
    color: $floating-label-color-active;
    font-weight: $floating-label-font-weight;
}

@mixin activeInput() {
    padding-bottom: 0.1rem;
    padding-top: 1rem;
    z-index: inherit;
}

input.floating-label {
    height: $floating-label-input-height-active;
    @include transition(padding ease-in-out 0.2s);
    @include disablePlaceholder;

    & + label {
        @include transition(all ease-in-out 0.2s);
        position: absolute;
        user-select: none;
        padding: 0.5rem 0.2rem;
        font-size: 1rem;
        line-height: 1.5;
        color: $floating-label-placeholder-color;
        pointer-events: none;
        top: 0.1rem;
        left: 0.6rem;
        margin: 0;
    }
}

input.floating-label:focus {
    @include activeInput;
    @include disablePlaceholder;

    + label {
        @include activeLabel;
    }
}

input.floating-label:not(:placeholder-shown),
input.floating-label:focus:not(:placeholder-shown) {
    @include activeInput;
}

input.floating-label:not(:placeholder-shown) + label,
input.floating-label:focus:not(:placeholder-shown) + label {
    @include activeLabel;
}
</style>
